import React from 'react';
import { motion } from 'framer-motion';
import { Utensils, Heart, Shield, Plane, GraduationCap } from 'lucide-react';

const impactAreas = [
  {
    id: 'feeding',
    title: 'Feeding Families',
    description: 'Weekly partnerships with Feeding America providing essential food assistance to families in need across the nation.',
    image: 'https://images.pexels.com/photos/6646886/pexels-photo-6646886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Utensils,
    stats: '12,500+ families fed',
    span: 'md:col-span-7 md:row-span-2',
  },
  {
    id: 'children',
    title: "Children's Healthcare",
    description: 'Supporting St. Jude and Children\'s Miracle Network Hospitals to give every child a fighting chance.',
    image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=800&q=80',
    icon: Heart,
    stats: '3,200+ children supported',
    span: 'md:col-span-5',
  },
  {
    id: 'veterans',
    title: 'Veterans Support',
    description: 'Honoring those who served through Wounded Warrior Project partnerships and direct assistance programs.',
    image: 'https://images.pexels.com/photos/7468032/pexels-photo-7468032.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Shield,
    stats: '850+ veterans assisted',
    span: 'md:col-span-5',
  },
  {
    id: 'travel',
    title: 'Travel Relief',
    description: 'Providing travel assistance and unforgettable experiences for military families and those in need.',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80',
    icon: Plane,
    stats: '425+ travel grants',
    span: 'md:col-span-6',
  },
  {
    id: 'mentorship',
    title: 'Mentorship Programs',
    description: 'Life skills training, financial literacy education, and professional development for sustainable change.',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
    icon: GraduationCap,
    stats: '15,000+ mentorship hours',
    span: 'md:col-span-6',
  },
];

const ImpactSection = () => {
  return (
    <section id="impact" className="py-24 bg-slate-50" data-testid="impact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
            Our Impact
          </p>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] tracking-tight mb-6">
            Where Your Support Goes
          </h2>
          <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
            Every contribution fuels real programs that transform lives. 
            Here's how we're making a difference together.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {impactAreas.map((area, idx) => (
            <motion.div
              key={area.id}
              className={`bento-card group ${area.span} min-h-[300px]`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.6 }}
              data-testid={`impact-card-${area.id}`}
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${area.image})` }}
              />
              
              {/* Overlay */}
              <div className="bento-card-overlay" />
              
              {/* Content */}
              <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <area.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/80 text-sm font-medium">{area.stats}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-white mb-2 font-serif">
                  {area.title}
                </h3>
                <p className="text-white/80 text-sm sm:text-base font-light">
                  {area.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
